import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../Components/Context/AuthContext";
import { downloadVisitPassPDF } from "../../Utils/GenerateVisitPassPdf";
import {
  FiDownload,
  FiUserCheck,
  FiMapPin,
  FiCalendar,
  FiClock,
  FiShield,
  FiCheckCircle,
  FiAlertTriangle,
  FiPackage,
  FiTruck,
  FiFilter,
} from "react-icons/fi";
import { TbLoader2 } from "react-icons/tb";

const OfmAvatar = ({ photo, name }) => {
  const [imgError, setImgError] = useState(false);

  const getInitials = (n) => {
    if (!n) return "OFM";
    const parts = n.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  if (photo && !imgError) {
    return (
      <img
        src={photo}
        alt={name || "OFM"}
        className="w-full h-full object-cover"
        onError={() => setImgError(true)}
      />
    );
  }

  return (
    <div className="w-full h-full bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-bold text-xs tracking-wider select-none">
      {getInitials(name)}
    </div>
  );
};

const PartnerGatePasses = () => {
  const { token, user } = useAuth();
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all"); // 'all' | 'order' | 'manual'
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'active' | 'expired'
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchPasses = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/client-visit-pass/partner/my-gate-passes`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.data?.success) {
        setPasses(res.data.data || []);
      }
    } catch (err) {
      console.error("[PartnerGatePasses] Error fetching passes:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchPasses();
    }
  }, [token, fetchPasses]);

  const handleDownload = async (pass) => {
    try {
      setDownloadingId(pass._id);
      await downloadVisitPassPDF(pass);
    } catch (err) {
      console.error("[PartnerGatePasses] PDF download error:", err);
    } finally {
      setDownloadingId(null);
    }
  };

  // Counts for UI filters
  const totalCount = passes.length;
  const orderPassesCount = passes.filter((p) => !p.isManual && p.passType !== "Manual").length;
  const manualPassesCount = passes.filter((p) => p.isManual || p.passType === "Manual").length;

  const activeCount = passes.filter(
    (p) => p.status === "Active" && new Date(p.validity?.toDate) >= new Date()
  ).length;
  const expiredCount = passes.filter(
    (p) => p.status !== "Active" || new Date(p.validity?.toDate) < new Date()
  ).length;

  const filteredPasses = passes.filter((p) => {
    const isManual = p.isManual || p.passType === "Manual";
    const isActive = p.status === "Active" && new Date(p.validity?.toDate) >= new Date();

    // Type Filter
    if (typeFilter === "order" && isManual) return false;
    if (typeFilter === "manual" && !isManual) return false;

    // Status Filter
    if (statusFilter === "active" && !isActive) return false;
    if (statusFilter === "expired" && isActive) return false;

    return true;
  });

  const getPassBadge = (pass) => {
    const isManual = pass.isManual || pass.passType === "Manual";
    if (isManual) {
      return {
        text: "🟣 MANUAL PASS",
        classes: "bg-purple-100 text-purple-700 border-purple-200",
        icon: null,
      };
    }
    if (pass.passType === "Sample") {
      return {
        text: "📦 SAMPLE STAGE PASS",
        classes: "bg-amber-100 text-amber-800 border-amber-200",
        icon: <FiPackage className="text-xs inline mr-1" />,
      };
    }
    if (pass.passType === "Dispute") {
      return {
        text: "⚠️ DISPUTE PASS",
        classes: "bg-rose-100 text-rose-800 border-rose-200",
        icon: <FiAlertTriangle className="text-xs inline mr-1" />,
      };
    }
    return {
      text: "🚚 FULL ORDER PASS",
      classes: "bg-blue-100 text-blue-700 border-blue-200",
      icon: <FiTruck className="text-xs inline mr-1" />,
    };
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full space-y-6">
        {/* Page Header */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-primaryColor font-bold text-xs uppercase tracking-wider mb-1">
                <FiShield className="text-base" /> Facility Entry Authorization
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
                Authorized Gate-Passes
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Official visit passes and entry credentials issued for Order Fulfillment Managers (OFMs) visiting your facility
              </p>
            </div>

            {/* Filter Controls */}
            <div className="flex flex-wrap items-center gap-2">
              {/* Type Filter Buttons */}
              <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-semibold">
                <button
                  onClick={() => setTypeFilter("all")}
                  className={`px-3.5 py-1.5 rounded-lg transition ${typeFilter === "all"
                    ? "bg-white text-slate-900 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                  All Passes ({totalCount})
                </button>
                <button
                  onClick={() => setTypeFilter("order")}
                  className={`px-3.5 py-1.5 rounded-lg transition ${typeFilter === "order"
                    ? "bg-white text-blue-700 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                  Order Passes ({orderPassesCount})
                </button>
                <button
                  onClick={() => setTypeFilter("manual")}
                  className={`px-3.5 py-1.5 rounded-lg transition ${typeFilter === "manual"
                    ? "bg-white text-purple-700 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                  Manual Passes ({manualPassesCount})
                </button>
              </div>

              {/* Status Filter Dropdown / Buttons */}
              <div className="flex items-center bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-semibold">
                <button
                  onClick={() => setStatusFilter("all")}
                  className={`px-3 py-1.5 rounded-lg transition ${statusFilter === "all"
                    ? "bg-white text-slate-900 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                  All Status
                </button>
                <button
                  onClick={() => setStatusFilter("active")}
                  className={`px-3 py-1.5 rounded-lg transition ${statusFilter === "active"
                    ? "bg-white text-emerald-700 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                  Active ({activeCount})
                </button>
                <button
                  onClick={() => setStatusFilter("expired")}
                  className={`px-3 py-1.5 rounded-lg transition ${statusFilter === "expired"
                    ? "bg-white text-rose-700 shadow-sm font-bold"
                    : "text-slate-600 hover:text-slate-900"
                    }`}
                >
                  Inactive ({expiredCount})
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200">
            <TbLoader2 className="w-8 h-8 text-primaryColor animate-spin" />
            <p className="text-sm text-slate-500 mt-3 font-medium">
              Loading authorized Gate-Passes...
            </p>
          </div>
        ) : filteredPasses.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-dashed border-slate-300 text-center p-6">
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-4">
              <FiShield className="text-2xl" />
            </div>
            <h3 className="text-lg font-bold text-slate-800">No Gate-Passes Found</h3>
            <p className="text-xs sm:text-sm text-slate-500 max-w-md mt-1">
              {typeFilter !== "all" || statusFilter !== "all"
                ? "No gate-passes matched the selected filters. Try changing or clearing your filters."
                : "When PCM Exports assigns an OFM (Order Fulfillment Manager) to visit your facility for sample verification, full order quality audit, or general inspection, authorized Gate-Passes will appear here."}
            </p>
            {(typeFilter !== "all" || statusFilter !== "all") && (
              <button
                onClick={() => {
                  setTypeFilter("all");
                  setStatusFilter("all");
                }}
                className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition"
              >
                <FiFilter className="text-xs" /> Reset Filters
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPasses.map((pass) => {
              const isManual = pass.isManual || pass.passType === "Manual";
              const isPassActive =
                pass.status === "Active" && new Date(pass.validity?.toDate) >= new Date();
              const isDownloading = downloadingId === pass._id;
              const badgeInfo = getPassBadge(pass);
              const orderDoc = pass.orderId;

              return (
                <div
                  key={pass._id}
                  className={`bg-white rounded-2xl border transition-all hover:shadow-md flex flex-col justify-between overflow-hidden ${isManual ? "border-purple-200" : "border-slate-200"
                    } ${!isPassActive ? "opacity-90 bg-slate-50/50" : ""}`}
                >
                  {/* Card Header */}
                  <div className="p-5 border-b border-slate-100 flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                          Gate-Pass
                        </span>
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${badgeInfo.classes}`}
                        >
                          {badgeInfo.icon}
                          {badgeInfo.text}
                        </span>
                      </div>
                      <h2 className="text-base font-mono font-bold text-slate-900">
                        {pass.passNumber}
                      </h2>
                    </div>

                    <span
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${isPassActive
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-rose-50 text-rose-700 border border-rose-200"
                        }`}
                    >
                      {isPassActive ? (
                        <>
                          <FiCheckCircle className="text-xs" /> Active
                        </>
                      ) : (
                        <>
                          <FiAlertTriangle className="text-xs" /> {pass.status === "Revoked" ? "Revoked" : "Expired"}
                        </>
                      )}
                    </span>
                  </div>

                  {/* Card Body */}
                  <div className="p-5 space-y-4 flex-1">
                    {/* Order Reference Box (If Order-linked) */}
                    {!isManual && (orderDoc || pass.assignmentNumber) && (
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between gap-2 text-xs">
                        <div>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                            Order Reference
                          </p>
                          <p className="font-bold text-slate-900">
                            {orderDoc?.orderNumber || pass.assignmentNumber}
                          </p>
                        </div>
                        {orderDoc?.product?.name && (
                          <div className="text-right">
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                              Product
                            </p>
                            <p className="font-semibold text-slate-700 truncate max-w-[150px]">
                              {orderDoc.product.name}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Assigned OFM Profile Box */}
                    <div className="flex items-center gap-3 p-3.5 rounded-xl bg-blue-50/70 border border-blue-100">
                      <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center flex-shrink-0 font-bold text-sm shadow-sm overflow-hidden border border-blue-200">
                        <OfmAvatar
                          photo={
                            pass.ofmDetails?.photo ||
                            pass.ofmId?.profilePic ||
                            pass.ofmId?.identityVerification?.aadhaar?.photoUrl
                          }
                          name={
                            pass.ofmDetails?.name ||
                            (pass.ofmId
                              ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                              : "OFM")
                          }
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-extrabold text-blue-800 uppercase tracking-wider">
                          Assigned OFM Executive
                        </p>
                        <p className="text-sm font-bold text-slate-900 truncate">
                          {pass.ofmDetails?.name ||
                            (pass.ofmId
                              ? `${pass.ofmId.firstName || ""} ${pass.ofmId.lastName || ""}`.trim()
                              : "OFM Executive")}
                        </p>
                        <p className="text-xs text-slate-600">
                          📞 {pass.ofmDetails?.phone || pass.ofmId?.phone || "Contact on arrival"}
                        </p>
                      </div>
                    </div>

                    {/* Purpose & Assignment Details */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-1.5 text-slate-500 font-semibold mb-1">
                          <FiClock className="text-primaryColor" /> Purpose
                        </div>
                        <p className="text-slate-800 font-medium line-clamp-2">
                          {pass.assignmentDetails || pass.assignmentNumber || "General Quality Inspection"}
                        </p>
                      </div>

                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                        <div className="flex items-center gap-1.5 text-slate-500 font-semibold mb-1">
                          <FiCalendar className="text-primaryColor" /> Validity Period
                        </div>
                        <p
                          className={`font-bold ${isPassActive ? "text-slate-900" : "text-rose-600"
                            }`}
                        >
                          {pass.validity?.fromDate
                            ? new Date(pass.validity.fromDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : "—"}{" "}
                          to{" "}
                          {pass.validity?.toDate
                            ? new Date(pass.validity.toDate).toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            })
                            : "—"}
                        </p>
                      </div>
                    </div>

                    {/* Destination Facility */}
                    <div className="p-3 bg-amber-50/70 rounded-xl border border-amber-200/70 text-xs">
                      <div className="flex items-center gap-1.5 text-amber-900 font-bold mb-1">
                        <FiMapPin className="text-amber-700" /> Destination Facility
                      </div>
                      <p className="font-bold text-slate-900">
                        {pass.clientDetails?.companyName || user?.companyName || "Your Facility"}
                      </p>
                      <p className="text-amber-800 text-[11px] mt-0.5 break-words">
                        📍 {pass.clientDetails?.address || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-3">
                    <span className="text-[11px] text-slate-400 font-medium">
                      Issued by PCM Exports
                    </span>

                    <button
                      onClick={() => handleDownload(pass)}
                      disabled={isDownloading}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primaryColor text-white text-xs font-bold hover:bg-primaryColor/90 transition shadow-sm disabled:opacity-50"
                    >
                      {isDownloading ? (
                        <>
                          <TbLoader2 className="w-4 h-4 animate-spin" /> Generating PDF...
                        </>
                      ) : (
                        <>
                          <FiDownload className="text-sm" /> Download Gate-Pass PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PartnerGatePasses;
